// WHAT: Icon button component for triggering Withings smart sync operation
// WHY:  Provides compact UI element for syncing Withings data with minimal visual footprint
// DOMAIN: Health → Withings → Data Synchronization
// DEPENDENCIES: WithingsSmartSyncService, useGlobalProgress context, debugLog utility
// RISKS:
//   - NOTE: Only supports smart sync variant - no force sync option available
//   - NOTE: Error handling only logs to debug - user may not see sync failures
//   - WARN: Icon button uses emoji - may not render consistently across platforms
//   - NOTE: Loading state uses custom spinner - may not match design system

import { type FC, useState } from "react";

import { debugLog } from "@/domains/logging/utils/debugLog";
import { useGlobalProgress } from "@/shared/contexts";

import { WithingsSmartSyncService } from "../../services/withingsSmartSync";
import { WithingsSyncLoader } from "./WithingsLoader";

interface WithingsSyncIconButtonProperties {
  className?: string;
  onSyncComplete?: () => void;
}

/**
 * WHAT:
 *   Compact icon button component for triggering Withings smart sync operation.
 *
 * WHY:
 *   Provides minimal UI footprint for syncing Withings data in space-constrained layouts.
 *
 * PROPS:
 *   - onSyncComplete?: () => void — Optional callback executed after successful sync
 *   - className?: string — Optional CSS classes for styling
 *
 * BEHAVIOR:
 *   - Shows loading spinner during sync operation
 *   - Displays page-level loader overlay during sync
 *   - Integrates with global progress context for footer progress indicator
 *   - Logs all sync operations to debug system
 *   - Executes optional callback after successful sync completion
 *   - Only supports smart sync variant (no force sync option)
 *
 * RISKS:
 *   - NOTE: Only supports smart sync variant - no force sync option available
 *   - NOTE: Error handling only logs to debug - user may not see sync failures
 *   - WARN: Icon button uses emoji - may not render consistently across platforms
 *   - NOTE: Loading state uses custom spinner - may not match design system
 *   - WARN: No retry mechanism - failed syncs require manual retry
 *
 * @param root0
 *
 * @param root0.onSyncComplete
 *
 * @param root0.className
 *
 * @returns {JSX.Element} Withings sync icon button component
 *
 */
export const WithingsSyncIconButton: FC<WithingsSyncIconButtonProperties> = ({
  className = "",
  onSyncComplete,
}) => {
  const { endSync, startSync } = useGlobalProgress();
  const [loading, setLoading] = useState(false);
  const [showPageLoader, setShowPageLoader] = useState(false);

  const handleClick = async () => {
    debugLog("info", "health", "Withings sync icon button clicked", {
      file: "WithingsSyncIconButton.tsx",
      function: "handleClick",
      module: "withings",
    });

    setLoading(true);
    setShowPageLoader(true); // PageLoader anzeigen
    startSync(); // Footer-Loader aktivieren

    try {
      debugLog("info", "health", "Calling smart sync command", {
        file: "WithingsSyncIconButton.tsx",
        function: "handleClick",
        module: "withings",
      });

      const result = await WithingsSmartSyncService.smartSync();

      debugLog("info", "health", "Withings sync command completed", {
        file: "WithingsSyncIconButton.tsx",
        function: "handleClick",
        module: "withings",
        result: {
          newMeasurements: result.newMeasurements,
          success: result.success,
          totalMeasurements: result.totalMeasurements,
        },
      });

      // Callback für UI-Update
      if (onSyncComplete) {
        onSyncComplete();
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : String(error);
      debugLog("error", "health", `Withings sync command failed: ${errorMessage}`, {
        file: "WithingsSyncIconButton.tsx",
        function: "handleClick",
        module: "withings",
      });
    } finally {
      setLoading(false);
      setShowPageLoader(false); // PageLoader ausblenden
      endSync(); // Footer-Loader deaktivieren
    }
  };

  return (
    <>
      {showPageLoader ? <WithingsSyncLoader message="Smart Sync läuft..." /> : null}

      <button
        className={`flex items-center justify-center w-8 h-8 rounded-full bg-transparent hover:bg-neutral-700 text-neutral-400 hover:text-white transition-colors disabled:opacity-50 ${className}`}
        disabled={loading}
        onClick={() => {
          void handleClick();
        }}
        title="Withings synchronisieren"
      >
        {loading ? (
          <div className="w-4 h-4 border-2 border-neutral-400 border-t-transparent rounded-full animate-spin" />
        ) : (
          <span className="text-sm">🔄</span>
        )}
      </button>
    </>
  );
};
